/*
 * Description:
 * ---------------------
 * The Firewall screen's Activity: a sentence first, what came in uninvited
 * and was turned away and what the box's own devices were stopped from
 * doing, then a count per kind, each with what it usually is and whether it
 * needs anything. Under it the record of the latest blocked tries
 * (cloud-anchor/pkg/firewall blocklog.go, /firewall/log), each with when,
 * what tried, where to and what stopped it, and Ada to explain one; and the
 * log server every try is also sent to, a firewall setting like any other.
 * Wording in firewallWords.js.
 *
 * ! file components/FirewallActivity.js
 */
import React, { useState } from "react";
import useEdgeFeature, { FEATURE_PRESENT } from "../hooks/useEdgeFeature";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { askAda } from "../utils/askAda";
import { AiIcon } from "./AiIcon";
import { deviceName } from "./FirewallDevices";
import * as W from "./firewallWords";

const at = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = d.toDateString() === new Date().toDateString();
  return d.toLocaleString([], today
    ? { hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }
    : { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
};

const end = (addr, port) => (port ? `${addr}:${port}` : addr);

/* The latest tries, newest first, with a filter by what stopped them. */
function Record({ selectedNode, view, rows, groups }) {
  const [why, setWhy] = useState("");
  const log = useEdgeFeature(selectedNode, `/firewall/log?limit=200${why ? `&why=${encodeURIComponent(why)}` : ""}`);
  useVisibleInterval(log.refresh, 15000, log.state === FEATURE_PRESENT);
  const record = log.state === FEATURE_PRESENT && log.payload?.available ? log.payload : null;
  const entries = record?.entries || [];
  const missed = record ? W.LOG.missed(record.lost, record.unwritten) : "";
  return (
    <section className="fw-card">
      <header className="fw-card-head is-split">
        <div>
          <h3 className="fw-card-title">{W.LOG.title}</h3>
          <p>{W.LOG.means}</p>
        </div>
        <select className="fw-input fw-filter" value={why} onChange={(e) => setWhy(e.target.value)} aria-label="Show">
          {W.LOG.filters.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </header>
      {!record && <p className="fw-muted">{log.state === FEATURE_PRESENT ? W.LOG.unavailable : "Reading…"}</p>}
      {record && entries.length === 0 && <p className="fw-muted">{W.LOG.none}</p>}
      {entries.length > 0 && (
        <div className="fw-log-wrap">
          <table className="fw-log">
            <thead>
              <tr><th>{W.LOG.cols.when}</th><th>{W.LOG.cols.from}</th><th>{W.LOG.cols.to}</th><th>{W.LOG.cols.why}</th><th aria-label="Explain" /></tr>
            </thead>
            <tbody>
              {entries.map((e, i) => {
                const words = W.whyWords(e.why, view.rules, groups);
                const name = e.device ? deviceName(rows, e.device) : "";
                return (
                  <tr key={`${e.at}-${i}`}>
                    <td className="fw-log-when">{at(e.at)}</td>
                    <td>{name ? <><strong>{name}</strong> <small>{end(e.src, e.src_port)}</small></> : end(e.src, e.src_port)}</td>
                    <td>{end(e.dst, e.dst_port)} <small>{e.proto}</small></td>
                    <td><span className={`fw-why is-${W.whyTone(e.why, !!e.device)}`}>{words}</span></td>
                    <td>
                      <button type="button" className="fw-icon-btn is-ai" aria-label={W.LOG.explain} title={W.LOG.explain}
                        onClick={() => askAda(selectedNode, W.logQuestion(e, words), { send: true })}>
                        <AiIcon size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {missed && <p className="fw-inline-note is-warn">{missed}</p>}
      {record && <p className="fw-muted">{W.LOG.rate}</p>}
    </section>
  );
}

/* Where every try is also sent: the operator's own log server. */
function LogServer({ view, settings, locked, apply }) {
  const [server, setServer] = useState(view.log_server || "");
  const save = async (e) => {
    e.preventDefault();
    await apply({ ...settings, log_server: server.trim() });
  };
  return (
    <section className="fw-card">
      <header className="fw-card-head">
        <h3 className="fw-card-title">{W.LOG.server}</h3>
        <p>{W.LOG.serverMeans}</p>
      </header>
      <form className="fw-add" onSubmit={save}>
        <input value={server} onChange={(e) => setServer(e.target.value)} placeholder={W.LOG.serverPlaceholder} aria-label="Log server" />
        <button type="submit" className="fw-btn" disabled={locked || server.trim() === (view.log_server || "")}>{W.LOG.serverSave}</button>
        {view.log_server && (
          <button type="button" className="fw-btn" disabled={locked} onClick={() => { setServer(""); apply({ ...settings, log_server: "" }); }}>
            {W.LOG.serverClear}
          </button>
        )}
      </form>
      {view.log_server && <p className="fw-muted">{W.LOG.serverNow(view.log_server)}</p>}
    </section>
  );
}

export default function FirewallActivity({ selectedNode, view, settings, rows, groups, locked, apply }) {
  const tally = W.activityRows(view, (id) => groups.find((g) => g.id === id)?.name);
  return (
    <div className="fw-activity">
      <section className="fw-card">
        <header className="fw-card-head">
          <h3 className="fw-card-title">What it blocked</h3>
          <p className="fw-lead">{W.activityLead(view)}</p>
        </header>
        {tally.length > 0 && (
          <ul className="fw-tally">
            {tally.map((r) => (
              <li key={r.key} className={`is-${r.tone}`}>
                <span className="fw-tally-n">{W.count(r.n)}</span>
                <div>
                  <strong>{r.what}</strong>
                  <p>{r.why}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <Record selectedNode={selectedNode} view={view} rows={rows} groups={groups} />
      <LogServer view={view} settings={settings} locked={locked} apply={apply} />
    </div>
  );
}
